import { makeAutoObservable, runInAction } from 'mobx';
import { FEATURES } from '../../../models/Plan';
import { IReturnReason } from '../../../models/ReturnReasonTypes';
import ReturnReasonsService, { IReturnReasonsSettings } from '../../../services/api/return-reasons-service';

import { rootContainer, TYPE } from '../../../services/container/root';
import { uuid } from '../../../services/helpers';
import { IReorderedItem } from '../../../stores/types';
import portalStore from '../../../stores/current-portal-store';
import FollowUpQuestionStore from './follow-up-question-store';

import ReasonStore from './reason-store';
// import mock from './mocks/return-reasons.json';


export interface IReasonListStore {
    returnReasons: ReasonStore[];
    settings: IReturnReasonsSettings;
    get selectedReturnReason(): ReasonStore | undefined;
    setSelectedReasonId(reasonId: number|string): void;
    load(): void;
    get isLoading(): boolean;
}

class ReasonListStore implements IReasonListStore {
	returnReasons: ReasonStore[] = [];
	settings: IReturnReasonsSettings = {} as IReturnReasonsSettings;
    private _selectedReasonId?: number|string;
    private _selectedQuestionId?: number|string;
    private _isLoading: boolean = false;

    public get selectedReturnReason(): ReasonStore|undefined {
        return this.returnReasons.find(r => r.isIdEquals(this._selectedReasonId));
    }

    public get selectedQuestion(): FollowUpQuestionStore|undefined {
        return this.selectedReturnReason?.followUpQuestions?.find(r => r.isIdEquals(this._selectedQuestionId));
    }

    public addReason(): IReturnReason|any {
        const suffix = '_#' + uuid().split('-')[4];
        const newWeight = this.getNewWeight();

        const newReason = new ReasonStore(this, this._discardReason, {} as IReturnReason, uuid());
        newReason.setChanges({
            type: undefined,
            display_name: 'GENERIC_REASON' + suffix,
            is_enabled: false,
            weight: newWeight,
            follow_up_questions: [],
            is_eligible_for_switch_to_exchange: false,
        });

        this.returnReasons.push(newReason);

        return newReason.id;
    }

    private getNewWeight(): number {
        if (this.returnReasons.length === 0) {
            return 1;
        }

        return Math.max(...this.returnReasons.map(r => r.weight)) + 1;
    }

    public get isLoading(): boolean {
        return this._isLoading;
    }

	private get returnReasonsService(): ReturnReasonsService {
		return rootContainer.get<ReturnReasonsService>(TYPE.ReturnReasonsService);
	}

	constructor() {
        this._discardReason = this._discardReason.bind(this);
		makeAutoObservable(this);
	}

    async load() {
        this._isLoading = true;
		const [returnReasons] = await Promise.all([
            this.returnReasonsService.getReturnReasonsList(), 
            portalStore.loadFeatures([
                FEATURES.VIDEO_UPLOAD,
                FEATURES.PRODUCT_EXCHANGE, 
                FEATURES.SWITCH_TO_EXCHANGE, 
                FEATURES.ANY_TO_ANY_EXCHANGES_FEATURE
            ]),
        ]);
		runInAction(() => {
            this._isLoading = false;
			this.returnReasons = returnReasons.return_reasons.map(model => new ReasonStore(this, this._discardReason, model));
			this.settings = returnReasons.settings;
		});
	}

    public setSelectedReasonId(reasonId: number|string) {
        this._selectedReasonId = reasonId;
    }

    public setSelectedQuestionId(fuqId: number|string) {
        this._selectedQuestionId = fuqId;
    }

    public async saveNewWeights() {        
        const reorderedReasons = this.returnReasons.map(reasonStore => {
            return {
                id: reasonStore.id,
                weight: reasonStore.weight
            } as IReorderedItem;
        });

        await this.returnReasonsService.saveNewWeights(reorderedReasons);
        runInAction(() => {
            this.returnReasons.forEach(reason => {
                reason.commitChanges();
            });
        })
    }

    public deleteReason(store: ReasonStore) {
        this._discardReason(store);
    }

    /** removes newly created reason store after it was discarded */
    private _discardReason(store: ReasonStore): void {
        const storeIndex = this.returnReasons.findIndex(r => r === store);
        if (storeIndex < 0) {
            /** should never happen */
            throw new Error('discardReason did not find the store to remove');
        }
        this.returnReasons.splice(storeIndex, 1);
    }

}

export default new ReasonListStore();
